import { tariffCommentDraftSchema } from './comment';
import { z } from 'zod';
import { provincesEnum } from './payor';

export const claimTariffStatusEnumValues = [
  'Resubmitted',
  'Incomplete',
  'Ready',
  'Submitted',
  'Cancelled',
  'Pending',
  'Rejected',
  'Underpaid',
  'Overpaid',
  'Paid',
  'Queried',
  'Uninsured',
  'Payor Invoiced',
  'Reverted',
  'Pending Write-Off',
  'Unmatched',
  'Historical',
  'Written-Off',
  'Unclaimable',
] as const;

export type ClaimTariffStatusValue =
  (typeof claimTariffStatusEnumValues)[number];

export const CLAIM_TARIFF_STATUS = {
  RESUBMITTED: 'Resubmitted',
  INCOMPLETE: 'Incomplete',
  READY: 'Ready',
  SUBMITTED: 'Submitted',
  CANCELLED: 'Cancelled',
  PENDING: 'Pending',
  REJECTED: 'Rejected',
  UNDERPAID: 'Underpaid',
  OVERPAID: 'Overpaid',
  PAID: 'Paid',
  QUERIED: 'Queried',
  UNINSURED: 'Uninsured',
  PAYOR_INVOICED: 'Payor Invoiced',
  REVERTED: 'Reverted',
  PENDING_WRITE_OFF: 'Pending Write-Off',
  UNMATCHED: 'Unmatched',
  HISTORICAL: 'Historical',
  WRITTEN_OFF: 'Written-Off',
  UNCLAIMABLE: 'Unclaimable',
} as const satisfies Record<string, ClaimTariffStatusValue>;

export const claimTariffStatusBandEnumValues = [
  'Pre-submission',
  'Outstanding',
  'Completed',
  'Not counted',
] as const;

export type ClaimTariffStatusBandValue =
  (typeof claimTariffStatusBandEnumValues)[number];

export const CLAIM_TARIFF_STATUS_BAND: Record<
  string,
  ClaimTariffStatusBandValue
> = {
  PRE_SUBMISSION: 'Pre-submission',
  OUTSTANDING: 'Outstanding',
  COMPLETED: 'Completed',
  NOT_COUNTED: 'Not counted',
} as const;

export const claimTariffStatusBandSchema = z.enum(
  claimTariffStatusBandEnumValues
);

export const PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID =
  'tariffs.dashboardBilledDate';

export const READY_SUBMISSION_FILTER_ID = 'tariffs.readySubmitted';
export const SUBMITTED_READY_COUNT_KEY = 'ReadySubmitted';

export const claimTariffStatusSchema = z.enum(claimTariffStatusEnumValues);

export const UNRECONCILED_CLAIM_TARIFF_STATUSES = [
  CLAIM_TARIFF_STATUS.UNDERPAID,
  CLAIM_TARIFF_STATUS.OVERPAID,
] as const;

export const AGING_CLAIM_TARIFF_STATUSES = [
  CLAIM_TARIFF_STATUS.READY,
  CLAIM_TARIFF_STATUS.INCOMPLETE,
  CLAIM_TARIFF_STATUS.REJECTED,
  ...UNRECONCILED_CLAIM_TARIFF_STATUSES,
  CLAIM_TARIFF_STATUS.UNINSURED,
  CLAIM_TARIFF_STATUS.PAYOR_INVOICED,
] as const;

export const DEFAULT_CLAIM_TARIFF_STATUS: (typeof claimTariffStatusEnumValues)[number] =
  CLAIM_TARIFF_STATUS.READY;

const icd9Schema = z
  .string()
  .regex(
    /^(?!E)(?:[0-9]{3,5}|[Vv][0-9]{2,4})[bB]?$/,
    'icd9 must one of the following: 3-5 characters and cannot start with E'
  );

const optionalIcd9Schema = z.union([
  icd9Schema.optional().nullable(),
  z.literal(''),
]);

export const tariffSchema = z.object({
  id: z.guid(
    'One or more tariff(s) are invalid, please select a valid tariff from the list or remove it.'
  ),
  code: z.string().min(1, 'code cannot be empty'),
  prefix: z.number(),
  description: z.string().min(1, 'description cannot be empty'),
  effectiveDate: z.string(),
  specialtyCode: z.string().nullable(),
  province: provincesEnum,
  type: z.string(),
  rate: z.string(),
  anesthesiaServiceValue: z.string().optional().default('0'),
  applyLocationModifier: z.boolean().default(true),
  units: z.number().nullable(),
  icd9: optionalIcd9Schema,
  percentage: z.number().min(0).max(300).optional().nullable(),
  split: z
    .string()
    .regex(
      /^(A|C|D|E|G|H|K|L|N|P|S|T|U|V|W|X|[1-7]|TF|PF|CF|TA|VT| )$/,
      'split must be a supported provincial split or special circumstances code'
    )
    .default(' '),
  incision: z
    .string()
    .regex(
      /^(B|D|S|L|R| )$/,
      'incision must be a supported provincial incision or bilateral code'
    )
    .optional()
    .nullable(),
  anesthesiaUnits: z.number().min(0).max(96).optional().nullable(),
  total: z.number().optional().nullable(),
  isManualTotal: z.boolean().nullable().default(false),
  serviceStart: z.string().optional().nullable(),
  serviceEnd: z.string().optional().nullable(),
  status: claimTariffStatusSchema,
  lastSubmittedAt: z.string().nullable().optional(),
  submissionDates: z.array(z.string()).optional(),
  latestComment: z.string().nullable().optional(),
  pendingComment: tariffCommentDraftSchema.optional(),
});

export const tariffSummarySchema = tariffSchema
  .pick({
    id: true,
    prefix: true,
    code: true,
    description: true,
  })
  .strict();

export const tariffSchemaWithRequiredICD9 = tariffSchema.extend({
  icd9: icd9Schema,
});

export const tariffSchemaWithOptionalICD9 = tariffSchema;

export type ClaimTariff = z.infer<typeof tariffSchema>;

export const isTariff = (obj: any): obj is z.infer<typeof tariffSchema> => {
  return tariffSchema.safeParse(obj).success;
};
